import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../service/auth.service';
import { BillingService } from '../../service/billing.service';
import { SubscriptionStateService } from '../../service/subscription-state.service';
import { AccountModeService } from '../../service/account-mode.service';
import { BillingNotice } from '../../models/billing.model';
import { daysLeft, isExtensionPending, isInSoftLanding, isPaddleManaged, isSoftLandingOver, trialStartErrorKey } from '../../service/billing.util';
import { dismissToday, isDismissedToday } from './banner-dismiss';

export type BannerKind = 'none' | 'start' | 'trial' | 'extension' | 'soft' | 'grace' | 'expired' | 'ending' | null;

/** Deneme banner'ı son N günde görünür; öncesinde çip + dashboard kartı yeter (spec 2026-09-24 §3.3). */
export const TRIAL_BANNER_DAYS = 3;

/** Organizasyon (partner) hesabı ücretsizdir: deneme başlatma / abonelik yok bantları gösterilmez. */
const HIDDEN_FOR_ORGANIZATION: ReadonlySet<BannerKind> = new Set<BannerKind>(['start', 'none']);

/** "Bugün gizle" yalnız bu türlerde; karar anı bantları (start, soft, expired…) gizlenmez. */
const DISMISSIBLE: ReadonlySet<BannerKind> = new Set<BannerKind>(['trial', 'extension']);

/**
 * Abonelik global şeridi (restricted-banner deseni). Durumlar: none / start (denemesiz, uygun) /
 * trial (son 3 gün) / extension (açık uzatma talebi) / soft (yumuşak iniş) / grace (past_due) /
 * expired / ending (bitişe ≤30 gün, otomatik yenileme kapalı). Ayrıca 402 toast'ları (interceptor → SubscriptionStateService.paymentRequired$) ve
 * LAUNCH_NOTICE (pendingNotices → bir kez → Ack) buradan gösterilir; MessageService bileşen bazlı
 * olduğu için toast'ın sahibi bu bileşendir.
 *
 * Status alınamazsa (ör. şirketi olmayan kartexadmin) şerit gösterilmez — sunucu asıl bekçi.
 */
@Component({
    selector: 'app-subscription-banner',
    standalone: true,
    imports: [CommonModule, ButtonModule, DialogModule, ToastModule, TranslateModule],
    providers: [MessageService],
    template: `
        <p-toast key="subscription" position="top-center">
            <ng-template let-message pTemplate="message">
                <div class="flex flex-col gap-2 w-full">
                    <span>{{ message.detail }}</span>
                    <p-button *ngIf="message.data?.go" [label]="'COMMON.SUBSCRIPTION.BANNER_GO' | translate" size="small" (onClick)="goSubscription()" />
                </div>
            </ng-template>
        </p-toast>

        @if (kind(); as k) {
            <div class="subscription-banner" [class.is-danger]="k === 'expired'" [class.is-info]="k === 'trial' || k === 'none' || k === 'start' || k === 'extension'" [class.is-warn]="k === 'soft'" role="status">
                <div class="subscription-banner-message">
                    <i class="pi" [ngClass]="icon()"></i>
                    <span>{{ messageKey() | translate: messageParams() }}</span>
                </div>
                <div class="subscription-banner-actions">
                    @if (dismissible()) {
                        <p-button [label]="'COMMON.TRIAL.DISMISS_TODAY' | translate" [text]="true" size="small" (onClick)="dismiss()" data-testid="banner-dismiss" />
                    }
                    <p-button [label]="actionKey() | translate" icon="pi pi-arrow-right" size="small" [loading]="starting()" (onClick)="onAction()" data-testid="banner-action" />
                </div>
            </div>
        }

        <p-dialog [(visible)]="noticeVisible" [modal]="true" [closable]="false" [style]="{ width: '32rem', maxWidth: '95vw' }" [header]="'COMMON.SUBSCRIPTION.LAUNCH_NOTICE_TITLE' | translate">
            <p class="m-0 leading-relaxed">{{ 'COMMON.SUBSCRIPTION.LAUNCH_NOTICE' | translate }}</p>
            <ng-template pTemplate="footer">
                <p-button [label]="'COMMON.CLOSE' | translate" (onClick)="ackNotice()" [loading]="acking()" />
            </ng-template>
        </p-dialog>
    `,
    styles: [
        `
            .subscription-banner {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                justify-content: space-between;
                gap: 0.75rem;
                padding: 0.75rem 1.25rem;
                margin: 0.75rem;
                border-radius: 8px;
                background: rgba(234, 179, 8, 0.12);
                border: 1px solid rgba(234, 179, 8, 0.55);
                color: #713f12;
            }
            .subscription-banner.is-danger {
                background: rgba(239, 68, 68, 0.1);
                border-color: rgba(239, 68, 68, 0.5);
                color: #7f1d1d;
            }
            .subscription-banner.is-info {
                background: rgba(59, 130, 246, 0.1);
                border-color: rgba(59, 130, 246, 0.5);
                color: #1e3a8a;
            }
            .subscription-banner.is-warn {
                background: rgba(249, 115, 22, 0.1);
                border-color: rgba(249, 115, 22, 0.5);
                color: #7c2d12;
            }
            :host-context(.app-dark) .subscription-banner {
                color: #fde68a;
            }
            :host-context(.app-dark) .subscription-banner.is-danger {
                color: #fecaca;
            }
            :host-context(.app-dark) .subscription-banner.is-info {
                color: #bfdbfe;
            }
            :host-context(.app-dark) .subscription-banner.is-warn {
                color: #fed7aa;
            }
            .subscription-banner-message {
                display: flex;
                align-items: center;
                gap: 0.6rem;
                font-size: 0.9rem;
            }
            .subscription-banner-message i {
                font-size: 1.1rem;
            }
            .subscription-banner-actions {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                flex-wrap: wrap;
            }
        `
    ]
})
export class SubscriptionBannerComponent implements OnInit {
    private auth = inject(AuthService);
    private router = inject(Router);
    private billing = inject(BillingService);
    private messages = inject(MessageService);
    private translate = inject(TranslateService);
    readonly state = inject(SubscriptionStateService);
    private readonly accountMode = inject(AccountModeService);
    /** Menüdeki sade (organizasyon) kararıyla aynı sinyal: AccountModeService.mode$. */
    private readonly mode = toSignal(this.accountMode.mode$, { initialValue: this.accountMode.mode() });

    noticeVisible = false;
    readonly acking = signal(false);
    readonly starting = signal(false);
    /** Bu oturumda gizlenen türler; localStorage yazılamasa da bant o oturumda kapanır. */
    private readonly dismissedNow = signal<ReadonlySet<BannerKind>>(new Set());
    private notice: BillingNotice | null = null;

    /**
     * Durum tabanlı şerit. Bitiş şeridi bunun ÜSTÜNE değil, ALTINA bakar: her bandın tek
     * sahibi vardır, yani deneme/gecikme/bitmiş abonelik şeridi varken bitiş şeridi çıkmaz.
     */
    private readonly statusKind = computed<BannerKind>(() => {
        if (!this.state.loaded() || this.state.statusFailed()) return null;
        const s = this.state.status();
        if (!s) return null;
        const st = this.state.normalizedStatus();
        // Düzeltme turu 1, bulgu 1: sunucu `trial.daysLeft`i negatif dönebilir; banda 0'ın altı sızmasın.
        const left = s.trial?.daysLeft != null ? Math.max(0, s.trial.daysLeft) : daysLeft(s.periodEnd);

        // Final inceleme C1: deneme bitince sunucu `status: null` (→ 'None') döner, `softLandingUntil`i
        // yine doldurur. Yumuşak iniş durumdan önce okunur; yoksa turuncu bant hiç görünmez.
        if ((st === 'None' || st === 'Expired') && isInSoftLanding(s)) return 'soft';

        switch (st) {
            case 'None':
                // Spec §3.3 "yumuşak iniş sonrası → mevcut süresi dolmuş bandı": geçmiş tarihli
                // `softLandingUntil` biten deneme demektir, genel "abonelik yok" metni değil.
                if (isSoftLandingOver(s)) return 'expired';

                // 2026-10-14: süre sunucudan (trialOffer.days); teklif yoksa (eski API) deneme bandı
                // gösterilmez, sayısız cümle uydurulmaz.
                if (s.trialEligible === true) return s.trialOffer?.days ? 'start' : null;

                return 'none';
            case 'Trial':
                if (isExtensionPending(s)) return 'extension';

                return left !== null && left <= TRIAL_BANNER_DAYS ? 'trial' : null;
            case 'Grace':
            case 'PastDue':
                return 'grace';
            case 'Paused':
                return 'grace';
            case 'Expired':
                return 'expired';
            case 'Refunded':
                return 'expired';
            case 'Cancelled':
                return this.state.entitled() ? null : 'expired';
            default:
                return s.pastDue ? 'grace' : null;
        }
    });

    /**
     * PO kararı 2026-09-17 (C): bitiş şeridi her girişte değil, yalnız sunucu `endingSoon`
     * derse görünür (bitişe ≤30 gün VE otomatik yenileme kapalı). Kural sunucuda tek yerde;
     * panel tarihe bakıp kendi hesabını YAPMAZ, yoksa iki kural zamanla ayrışır.
     */
    readonly kind = computed<BannerKind>(() => {
        const base = this.statusKind();

        if (base && this.mode() === 'organization' && HIDDEN_FOR_ORGANIZATION.has(base)) return null;
        if (base) return DISMISSIBLE.has(base) && this.isHidden(base) ? null : base;
        if (!this.state.loaded() || this.state.statusFailed()) return null;

        return this.state.status()?.endingSoon ? 'ending' : null;
    });

    readonly dismissible = computed(() => DISMISSIBLE.has(this.kind()));

    readonly icon = computed(() => {
        switch (this.kind()) {
            case 'start':
                return 'pi-gift';
            case 'trial':
            case 'soft':
                return 'pi-clock';
            case 'extension':
                return 'pi-hourglass';
            case 'ending':
                return 'pi-calendar';
            case 'grace':
                return 'pi-exclamation-triangle';
            default:
                return 'pi-credit-card';
        }
    });

    readonly messageKey = computed(() => {
        switch (this.kind()) {
            case 'none':
                return 'COMMON.SUBSCRIPTION.BANNER_NONE';
            case 'start':
                return 'COMMON.TRIAL.START_BANNER';
            case 'extension':
                return 'COMMON.TRIAL.EXTENSION_PENDING_BANNER';
            case 'soft':
                return 'COMMON.TRIAL.SOFT_LANDING_BANNER';
            case 'trial':
                return this.trialChargesOn() ? 'COMMON.SUBSCRIPTION.TRIAL_CHARGES_ON' : 'COMMON.SUBSCRIPTION.TRIAL_DAYS_LEFT';
            case 'grace':
                return this.state.normalizedStatus() === 'Paused' ? 'COMMON.SUBSCRIPTION.PAUSED' : 'COMMON.SUBSCRIPTION.PAST_DUE';
            case 'ending':
                return 'COMMON.SUBSCRIPTION.ENDING_SOON';
            default:
                // Yumuşak inişi bitmiş deneme: kartlar profilde gizli, veri duruyor (spec §2.4, WindDownEnded
                // postası). Genel BANNER_EXPIRED "kartlar kullanılıyor" der; burada doğru olmaz.
                return isSoftLandingOver(this.state.status()) ? 'COMMON.TRIAL.WIND_DOWN_BANNER' : 'COMMON.SUBSCRIPTION.BANNER_EXPIRED';
        }
    });

    /** Paddle denemesi (kart kayıtlı, autoRenew): "deneme bitiyor" yerine "{date} tarihinde ücretlendirilecek". */
    readonly trialChargesOn = computed(() => {
        const s = this.state.status();

        return isPaddleManaged(s) && !!s?.autoRenew && !s?.scheduledCancel && !!s?.periodEnd;
    });

    readonly messageParams = computed<Record<string, unknown>>(() => {
        const s = this.state.status();
        const lang = this.translate.currentLang || 'tr';

        switch (this.kind()) {
            case 'start':
                return { days: s?.trialOffer?.days };
            case 'extension':
                return { date: this.formatDate(s?.extension?.autoApproveAt, lang) };
            case 'soft':
                return { days: daysLeft(s?.softLandingUntil) ?? 0 };
            default: {
                // Gün sayısı: deneme > sunucunun `daysLeft`i > yerel hesap. Sunucu bitiş şeridinde
                // kendi gününü döner; yerel hesap yalnız eski yanıtlar için yedek.
                // Düzeltme turu 1, bulgu 1: negatif gün metne sızmasın, 0'da taban.
                return { days: Math.max(0, s?.trial?.daysLeft ?? s?.daysLeft ?? daysLeft(s?.periodEnd) ?? 0), date: this.formatDate(s?.periodEnd, lang) };
            }
        }
    });

    readonly actionKey = computed(() => {
        switch (this.kind()) {
            case 'start':
                return 'COMMON.TRIAL.START_ACTION';
            case 'trial':
            case 'soft':
                return 'COMMON.TRIAL.BUY_ACTION';
            case 'grace':
                return this.state.normalizedStatus() !== 'Paused' ? 'COMMON.SUBSCRIPTION.UPDATE_PAYMENT' : 'COMMON.SUBSCRIPTION.BANNER_GO';
            default:
                return 'COMMON.SUBSCRIPTION.BANNER_GO';
        }
    });

    constructor() {
        this.state.paymentRequired$.pipe(takeUntilDestroyed()).subscribe((ev) => {
            this.messages.add({
                key: 'subscription',
                severity: 'warn',
                summary: this.translate.instant('COMMON.SUBSCRIPTION.TITLE'),
                detail: this.translate.instant(ev.i18nKey, ev.params),
                life: 8000,
                data: { go: ev.suggestSubscription && !this.router.url.startsWith('/subscription') }
            });
        });
    }

    ngOnInit(): void {
        if (!this.auth.hasValidTokens()) return;
        this.state.refreshMe().subscribe({
            next: (me) => {
                const n = (me.pendingNotices ?? []).find((x) => (x.kind ?? '').toLowerCase().replace(/[_-]/g, '') === 'launchnotice');

                if (n) {
                    this.notice = n;
                    this.noticeVisible = true;
                }

                // Panel bulgu duzeltme (bulgu #3): Enterprise/Status yalniz sirket hesabinda (OwnerType 2)
                // anlamlidir; kisi hesabinda o uc kalici olarak 400 NotEnterpriseAccount doner (bkz.
                // BillingEndpoints.ResolveCompanyAsync, bilincli ve kalici bekci). Her sayfada bu ucu
                // korusuzca cagirmak kisi hesaplarinda rutin 400 uretiyordu; simdi yalniz sirket
                // hesabinda cagrilir.
                if (me.ownerType === 2) this.state.refresh().subscribe({ error: () => undefined });
            },
            error: () => undefined
        });
    }

    goSubscription(): void {
        this.messages.clear('subscription');
        this.router.navigate(['/subscription']);
    }

    /** Bant düğmesi: "start" denemeyi tek tıkla başlatır, diğerleri /subscription'a gider. */
    onAction(): void {
        if (this.kind() !== 'start') {
            this.goSubscription();

            return;
        }
        // Düzeltme turu 1, bulgu 1: [loading] yalnız görsel devre dışı bırakır; hızlı art arda
        // tıklamada (ör. çift dokunma) ikinci istek açılmasın diye burada da kilitliyoruz.
        if (this.starting()) return;
        this.starting.set(true);
        this.billing.startTrial().subscribe({
            next: (s) => {
                this.starting.set(false);
                this.state.apply(s);
                this.messages.add({ key: 'subscription', severity: 'success', summary: this.translate.instant('COMMON.SUBSCRIPTION.TITLE'), detail: this.translate.instant('COMMON.TRIAL.STARTED'), life: 5000 });
            },
            error: (err) => {
                this.starting.set(false);
                console.error('Deneme başlatılamadı', err);
                // Uygunluk değişmiş olabilir (ör. TrialAlreadyUsed): mesajı göster, durumu tazele.
                // Eşleme ortak yardımcıda (TrialAlreadyUsed kendi anahtarı); bantta biçem her kodda uyarı.
                const key = trialStartErrorKey(err);
                this.messages.add({ key: 'subscription', severity: 'warn', summary: this.translate.instant('COMMON.SUBSCRIPTION.TITLE'), detail: this.translate.instant(key), life: 8000, data: { go: true } });
                this.state.refresh().subscribe({ error: () => undefined });
            }
        });
    }

    dismiss(): void {
        const k = this.kind();

        if (!k || !DISMISSIBLE.has(k)) return;
        dismissToday(k);
        this.dismissedNow.update((set) => new Set([...set, k]));
    }

    private isHidden(k: BannerKind): boolean {
        return this.dismissedNow().has(k) || (k !== null && isDismissedToday(k));
    }

    private formatDate(iso: string | null | undefined, lang: string): string {
        const d = iso ? new Date(iso) : null;

        return d && !isNaN(d.getTime()) ? d.toLocaleDateString(lang) : '';
    }

    ackNotice(): void {
        const n = this.notice;

        if (!n) {
            this.noticeVisible = false;

            return;
        }
        this.acking.set(true);
        this.billing.ackNotice(n.id).subscribe({
            next: () => {
                this.acking.set(false);
                this.noticeVisible = false;
                this.notice = null;
            },
            error: () => {
                // Ack başarısız olsa da kullanıcıyı kilitleme; bir sonraki girişte yeniden sorulur.
                this.acking.set(false);
                this.noticeVisible = false;
            }
        });
    }
}
